import * as React from 'react'

import includes from 'lodash/includes'

import { observer } from 'mobx-react'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import i18n from 'modules/core/lib/i18n'

import FieldSelector from 'modules/reports/components/Editor/Carcasa/FormFields/FieldSelector'
import type { LabelType } from 'modules/reports/components/Editor/Carcasa/FormFields/FieldSelector'
import type { FieldType } from 'modules/reports/components/Editor/Field/types'
import { useLoadingFields } from 'modules/reports/contexts/loadFieldsContext'
import useFields from 'modules/reports/hooks/useFields'
import { FieldSelectionType, useReportTracking } from 'modules/reports/hooks/useReportTracking'
import { ABILITIES, FieldValueType } from 'modules/reports/lib/Constants'
import type { ReportEditorStore } from 'modules/reports/lib/Store'
import { getDefaultAggregationFunction } from 'modules/reports/lib/fieldHelper'
import type Field from 'modules/reports/store/models/Field'
import type { AbilityType } from 'modules/reports/store/models/types'

interface Props {
  store: ReportEditorStore
  formIndex: number
  fieldAbilityTypes: AbilityType[]
  label: LabelType
  buttonLabel: string
  modalDescription?: string
  excludeIdentifiers?: boolean
  excludeTypes?: FieldValueType[]
  selectionType: FieldSelectionType
}

const ProjectionField = ({
  store,
  formIndex,
  fieldAbilityTypes,
  modalDescription,
  excludeIdentifiers,
  excludeTypes,
  buttonLabel,
  label,
  selectionType,
}: Props) => {
  const loadFields = useFields()
  const [, setLoadingFields] = useLoadingFields()
  const { trackFieldChange } = useReportTracking(store)

  const updateRelationFields = useUnsafeCallback(() => {
    loadFields(
      { projections: store.projection.paths() },
      (status) => setLoadingFields(status),
      (status) => setLoadingFields(status)
    )
  }, [loadFields, setLoadingFields, store.projection])

  const onRemoveField = (_item: FieldType) => {
    store.projection.removeFormsFromIndexToEnd(formIndex)
    updateRelationFields()
  }

  const onSelectFields = (fields: Field[]) => {
    const field = fields[0]

    if (field) {
      trackFieldChange(selectionType)

      const supportsAggregation = includes(fieldAbilityTypes, ABILITIES.aggregation)
      store.projection.updateOrCreate(formIndex, {
        path: field.attributes.path,
        function: supportsAggregation ? getDefaultAggregationFunction(field) : null,
      })

      updateRelationFields()
    }
  }

  const onCloseFields = () => {
    updateRelationFields()
  }

  const defaultModalDescription = i18n.t('custom_reports.fields.group_by_modal_description')

  const fieldTypes = store.projection.selectedFieldTypes[formIndex]

  return (
    <FieldSelector
      variables={[]}
      disabled={!store.isFieldEnabled(formIndex)}
      fieldTypes={fieldTypes ? [fieldTypes] : []}
      fieldAbilityTypes={fieldAbilityTypes}
      label={label}
      onSelectFields={onSelectFields}
      onRemoveField={onRemoveField}
      excludeIdentifiers={excludeIdentifiers}
      excludeTypes={excludeTypes}
      chartType={store.reportStruct.type}
      buttonLabel={buttonLabel}
      modalTitle={i18n.t('custom_reports.fields.data')}
      modalDescription={modalDescription || defaultModalDescription}
      onCloseModal={onCloseFields}
    />
  )
}

export default observer(ProjectionField)
